import {
  Button,
  Modal,
  Step,
  StepContent,
  StepDescription,
  StepTitle,
} from "semantic-ui-react";
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

const MultipartForm = ({
  open,
  onClose,
  title,
  pages,
  initialState = {},
  size = "large",
  className,
  style,
  ...rest
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [formState, setFormState] = useState(initialState);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setCurrentPage(0);
      setFormState(initialState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const totalPages = pages.length;
  const currentPageData = pages[currentPage];

  const goToPrevious = () => setCurrentPage((prev) => Math.max(0, prev - 1));
  const goToNext = () =>
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  const finish = () => onClose();

  // Default actions
  const defaultActions = [
    {
      content: "Previous",
      onClick: (currentPage, setCurrentPage) =>
        setCurrentPage((prev) => Math.max(0, prev - 1)),
      disabled: (currentPage) => currentPage === 0,
      props: {},
    },
    {
      content: "Next",
      onClick: (currentPage, setCurrentPage) =>
        setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1)),
      disabled: (currentPage) => currentPage === totalPages - 1,
      props: {},
    },
    {
      content: "Finish",
      onClick: (currentPage, setCurrentPage) => onClose(),
      disabled: (currentPage) => currentPage !== totalPages - 1,
      props: { primary: true },
    },
  ];

  // Use page-specific actions if provided, otherwise use defaults
  const actions = currentPageData.actions || defaultActions;
  const content = React.useCallback(() => {
    return typeof currentPageData.content === "function"
      ? currentPageData.content(formState, setFormState)
      : currentPageData.content;
  }, [formState, currentPageData])();
  return (
    <Modal
      open={open}
      onClose={onClose}
      size={size}
      closeOnDimmerClick={false}
      closeOnDocumentClick={false}
      closeOnEscape={false}
      className={className}
      style={style}
      {...rest}
    >
      <Modal.Header>{title || currentPageData.title}</Modal.Header>
      <Step.Group ordered style={{ margin: "20px" }}>
        {pages.map((page, index) => (
          <Step
            key={index}
            active={index === currentPage}
            completed={
              formState.completedSteps?.includes(index) || index < currentPage
            }
          >
            <StepContent>
              <StepTitle>{page.stepTitle || page.title}</StepTitle>
              <StepDescription>{page.stepDescription}</StepDescription>
            </StepContent>
          </Step>
        ))}
      </Step.Group>
      <Modal.Content>{content}</Modal.Content>
      <Modal.Actions>
        {actions.map((action, index) => {
          const ButtonComponent = action.component || Button;
          const isDisabled =
            typeof action.disabled === "function"
              ? action.disabled(formState, currentPage)
              : action.disabled;
          const buttonContent =
            typeof action.content === "function"
              ? action.content(formState, currentPage)
              : action.content;

          return (
            <ButtonComponent
              key={index}
              onClick={() =>
                action.onClick(
                  currentPage,
                  setCurrentPage,
                  formState,
                  setFormState
                )
              }
              disabled={isDisabled}
              style={action.style}
              {...action.props}
            >
              {buttonContent}
            </ButtonComponent>
          );
        })}
      </Modal.Actions>
    </Modal>
  );
};

MultipartForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  initialState: PropTypes.object,
  size: PropTypes.oneOf(["mini", "tiny", "small", "large", "fullscreen"]),
  className: PropTypes.string,
  style: PropTypes.object,
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      stepTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      content: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
      actions: PropTypes.arrayOf(
        PropTypes.shape({
          content: PropTypes.node.isRequired,
          onClick: PropTypes.func.isRequired,
          disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
          component: PropTypes.elementType,
          style: PropTypes.object,
          props: PropTypes.object,
        })
      ),
    })
  ).isRequired,
};

export default MultipartForm;
